<template>
  <div class="homepage-view">
    <div v-if="!cms_page" class="homepage-view__hero-placeholder"></div>

    <!-- CMS top zone; remounted when its content changes (the hero isn't reactive) -->
    <content-blocks
      v-else
      :key="top_key"
      :blocks="cms_page.top"
      :site_blocks="site_blocks"
    />

    <!--booking engine (posizionato sotto la sezione top)-->
    <section
      v-if="is_authenticated()"
      class="section section--booking-engine"
    >
      <b-container>
        <booking-engine @search="search" />
      </b-container>
    </section>

    <!--loader-->
    <section
      v-if="is_authenticated() && searching"
      class="section section--loader"
    >
      <loader size="large" :show_timing_slot="true" />
    </section>

    <!-- contenuti del sito, sempre visibili -->
    <slot></slot>

    <template v-if="cms_page">
      <!-- Zone CMS left | main | right (le colonne laterali vuote collassano) -->
      <div
        class="cms-layout"
        :class="{
          'cms-layout--columns':
            cms_page.left.length || cms_page.right.length,
        }"
      >
        <aside v-if="cms_page.left.length" class="cms-layout__side cms-layout__side--left">
          <content-blocks :blocks="cms_page.left" :site_blocks="site_blocks" />
        </aside>
        <div class="cms-layout__main">
          <content-blocks :blocks="cms_page.main" :site_blocks="site_blocks" />
        </div>
        <aside v-if="cms_page.right.length" class="cms-layout__side cms-layout__side--right">
          <content-blocks :blocks="cms_page.right" :site_blocks="site_blocks" />
        </aside>
      </div>

      <!-- Zona CMS bottom -->
      <content-blocks :blocks="cms_page.bottom" :site_blocks="site_blocks" />
    </template>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import Vue from 'vue';
  import md5 from 'md5';
  import ContentBlocks from '@/components/blocks/content-blocks.vue';
  import {
    get_page,
    build_default_page,
    page_has_blocks,
    CMS_ROUTE_SLUGS,
  } from '@/components/blocks/content-utilities';
  import { get_brand_blocks } from '@/components/blocks/sections/script';

  /**
   * Homepage condivisa dai siti, composta dai blocchi della pagina CMS. Se il
   * CMS non ha la pagina (endpoint assente, errore, pagina nulla o vuota) si
   * inietta una pagina di default con i contenuti statici del sito: hero da
   * config (`sliderHome.*`) e i blocchi di `default_blocks` (default: i
   * contenuti statici del `brand`).
   *
   * Each CMS page is cached in `cms_pages` under its own slug; the page shown
   * is also stored under the route slug (`home`), where App reads it for the
   * prefooter and theme. The cached page is shown right away and each slug is
   * fetched once per mount, replacing the cache with the response.
   */
  export default {
    name: 'homepage',

    components: {
      'content-blocks': ContentBlocks,
    },

    props: {
      page_slug: {
        type: String,
        default: CMS_ROUTE_SLUGS.homepage,
      },
      // Sotto-chiave di `sliderHome` per l'hero della pagina di default
      slider_variant: {
        type: String,
        default: '',
      },
      // Chiave dei contenuti statici di default (sections/script)
      brand: {
        type: String,
        default: () => Vue.prototype.$config.site.brand,
      },
      // Blocchi della zona main della pagina di default, al posto di quelli del brand
      default_blocks: {
        type: Array,
        default: null,
      },
      // Blocchi del sito: type => { component, wrap }
      site_blocks: {
        type: Object,
        default: () => ({}),
      },
    },

    computed: {
      ...mapState(['searching', 'cms_pages']),
      cms_page() {
        const { cms_pages = {} } = this;
        return cms_pages[this.page_slug] || null;
      },
      top_key() {
        return `${this.cms_page.page.slug}:${md5(JSON.stringify(this.cms_page.top))}`;
      },
    },

    watch: {
      page_slug: 'load_cms_page',
    },

    created() {
      this.fetched_slugs = new Set();
      this.load_cms_page();
    },

    methods: {
      load_cms_page() {
        const slug = this.page_slug;
        this.show_cms_page(slug);
        if (this.fetched_slugs.has(slug)) {
          return;
        }
        this.fetched_slugs.add(slug);
        get_page(slug).then((page) => {
          let home = page_has_blocks(page) ? page : null;
          // tab changed meanwhile: show_cms_page builds the default on return
          if (!home && slug === this.page_slug) {
            home = this.get_default_page();
          }
          this.$store.commit('cms_pages', { ...this.cms_pages, [slug]: home });
        });
      },
      // Cached page, default page once the CMS has none, placeholder meanwhile
      show_cms_page(slug) {
        let page = (this.cms_pages || {})[slug] || null;
        if (!page && this.fetched_slugs.has(slug)) {
          page = this.get_default_page();
        }
        this.$store.commit('cms_pages', {
          ...this.cms_pages,
          [this.page_slug]: page,
        });
      },
      get_default_page() {
        let main = this.default_blocks;
        if (!main) {
          main = get_brand_blocks(
            this.brand,
            this.is_authenticated(),
            this.get_config()
          );
        }
        return build_default_page(this.page_slug, {
          top: [
            { component: 'slider', props: { sliderVariant: this.slider_variant } },
          ],
          main,
        });
      },
      search() {
        this.$emit('search');
        this.$router.push({ name: 'results' });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .homepage-view__hero-placeholder {
    height: 280px;

    @include media-breakpoint-up(md) {
      height: 320px;
    }
    @include media-breakpoint-up(lg) {
      height: 360px;
    }
  }

  .section--booking-engine {
    position: relative;
    z-index: 1;

    @include media-breakpoint-up(md) {
      margin-top: -33px;
    }
  }

  // TODO: side column widths to be defined
  .cms-layout {
    // flex only with side columns: a flex container stops the margins of the
    // main blocks from collapsing with the sections around it
    &--columns {
      display: flex;
      flex-direction: column;

      @include media-breakpoint-up(lg) {
        flex-direction: row;
      }
    }

    &__main {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__side {
      @include media-breakpoint-up(lg) {
        flex: 0 0 25%;
        max-width: 25%;
      }

      // content-blocks renders only a comment node when it has no blocks
      &:empty {
        display: none;
      }
    }
  }
</style>
